import { useEffect } from 'react';
import { useAuth } from './useAuth';
import type { Akzentstaffel } from '@/types/domain';

// Die Akzentfarbe der Oberfläche, belegt aus der angemeldeten Sitzung.
//
// Der ganze Farbwechsel hängt an vier CSS-Variablen. Tailwind setzt sie in
// jede Akzent-Klasse ein (siehe tailwind.config.js), die Rückfallwerte
// stehen in index.css und sind das bisherige Teal. Diese Datei ist die
// einzige Stelle, die sie überschreibt.
//
// Warum nicht an vielen Stellen einzeln eingreifen: es gibt rund 150
// Akzent-Fundstellen in 49 Dateien. Jede davon anzufassen hieße, sie beim
// nächsten neuen Knopf wieder zu vergessen. Eine Variable vergisst man
// nicht.
//
// Der Aufruf steht in PageLayout — und damit genau dort, wo er hingehört:
// die Anmeldeseite und die Druckansicht rendern PageLayout nicht
// (routes/__root.tsx) und bleiben deshalb bei Teal. Auf einem Bestellzettel
// hat die Farbe des gerade Angemeldeten nichts verloren.

const VARIABLEN: Record<keyof Akzentstaffel, string> = {
  basis: '--akzent',
  stark: '--akzent-stark',
  sanft: '--akzent-sanft',
  linie: '--akzent-linie'
};

/**
 * "#5A784B" → "90 120 75".
 *
 * Tailwind setzt die Variablen in `rgb(var(--akzent) / <alpha>)` ein, damit
 * `bg-akzent-sanft/40` weiter funktioniert. Deshalb Tripel statt Hexwert.
 * Gibt null zurück, wenn der Wert nicht wie eine Farbe aussieht — dann
 * bleibt die Variable ungesetzt und der Rückfall aus index.css greift.
 */
export function hexAlsTripel(hex: string): string | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

export function useAkzentFarbe(): void {
  const auth = useAuth();
  const akzent = auth.data?.user.akzent ?? null;

  useEffect(() => {
    const wurzel = document.documentElement;
    if (akzent === null) {
      // Zurück auf Teal. Das Entfernen ist wichtig, nicht das Setzen eines
      // Teal-Werts: sonst stünde derselbe Wert an zwei Stellen und liefe
      // beim nächsten Feilen an der Palette auseinander (Designprinzip 4).
      for (const name of Object.values(VARIABLEN)) wurzel.style.removeProperty(name);
      return;
    }
    for (const [feld, name] of Object.entries(VARIABLEN)) {
      const tripel = hexAlsTripel(akzent[feld as keyof Akzentstaffel]);
      if (tripel !== null) wurzel.style.setProperty(name, tripel);
    }
    return () => {
      for (const name of Object.values(VARIABLEN)) wurzel.style.removeProperty(name);
    };
  }, [akzent]);
}
